import { Button } from "@/components/ui/baseUi/button"
import { Text } from "@/components/ui/baseUi/text"
import { cn } from "@/modules/lib/utils"
import backgroundCornerIllustration from "@images/box/backgroundCornerIllustration.png"
import emptyIllustration from "@images/box/boxFinancesLiteIllustration.png"
import { LogoHustleUpSmall } from "@images/logoHustleUpSmall"
import { CheckmarkRoundIcon } from "@images/svgIcons/checkmarkRoundIcon"
import { InformationAlertIcon } from "@images/svgIcons/informationAlertIcon"
import { word } from "@modules/core/utils/i18n"
import { useBoxNavigate } from "@modules/navigation/hooks/useBoxNavigate"
import { Path } from "@modules/navigation/routes"
import { HUCardAlert } from "@modules/ui/components/huCardAlert"
import { HUImageView } from "@modules/ui/components/huImageView"
import { ArrowRightIcon } from "lucide-react"

type BoxFinancesLiteViewProps = {
  className?: string
} & React.ComponentProps<"div">

export function BoxFinancesLiteView({ className, ...props }: BoxFinancesLiteViewProps) {
  const boxNavigate = useBoxNavigate()

  const handleCreateStripeAccount = () => {
    boxNavigate(Path.Box.OnboardingStripe)
  }

  return (
    <div className={cn("flex h-full w-full flex-1", className)} {...props}>
      <div className="z-1 flex flex-1 flex-col items-center justify-between p-8">
        <div className="flex w-full max-w-250 flex-col items-center p-5">
          <div className="mb-10">
            <LogoHustleUpSmall width={60} height={60} color="currentColor" />
          </div>

          <Text fontStyle="HL" className="mb-6 text-center">
            {word("finances.lite.header")}
          </Text>

          <div className="mb-8 flex flex-col gap-4">
            <Text fontStyle="BL" className="text-center text-neutral-700">
              {word("finances.lite.subHeader")}
            </Text>
            <Text fontStyle="BL" className="text-center text-neutral-700">
              {word("finances.lite.subHeader2")}
            </Text>
          </div>

          <div
            className="mb-8 flex w-full flex-row flex-wrap justify-center xl:justify-between"
            style={{ maxWidth: "900px" }}
          >
            <div className="mr-4 flex" style={{ maxWidth: "400px" }}>
              <div className="flex flex-col gap-7">
                <div className="flex flex-col gap-5">
                  <Text fontStyle="TS" className="text-neutral-700">
                    {word("finances.lite.stripe.header")}
                  </Text>
                  <div className="flex flex-col gap-4">
                    <div className="flex items-center gap-2">
                      <CheckmarkRoundIcon color="currentColor" width={24} height={24} backgroundColor={"transparent"} />
                      <Text fontStyle="BM">{word("finances.lite.stripe.phoneNumber")}</Text>
                    </div>

                    <div className="flex items-center gap-2">
                      <CheckmarkRoundIcon color="currentColor" width={24} height={24} backgroundColor={"transparent"} />
                      <Text fontStyle="BM">{word("finances.lite.stripe.businessType")}</Text>
                    </div>

                    <div className="flex items-center gap-2">
                      <CheckmarkRoundIcon color="currentColor" width={24} height={24} backgroundColor={"transparent"} />
                      <Text fontStyle="BM">{word("finances.lite.stripe.bankAccountDetails")}</Text>
                    </div>

                    <div className="flex items-center gap-2">
                      <CheckmarkRoundIcon color="currentColor" width={24} height={24} backgroundColor={"transparent"} />
                      <Text fontStyle="BM">{word("finances.lite.stripe.personalInformation")}</Text>
                    </div>
                  </div>
                </div>

                <Button variant="primary" size="lg" onClick={handleCreateStripeAccount} className="w-fit">
                  {word("finances.lite.stripe.createButton")}
                  <ArrowRightIcon color="currentColor" />
                </Button>
              </div>
            </div>

            <div className="mt-4 hidden xl:flex">
              <HUImageView image={{ url: emptyIllustration, hash: "" }} width="395px" height="227px" />
            </div>
          </div>
        </div>

        <HUCardAlert
          title=""
          description={word("finances.lite.stripe.alert")}
          descriptionFontStyle="BL"
          icon={<InformationAlertIcon backgroundColor="transparent" color="currentColor" />}
          className="mb-7 flex max-w-225"
        />
      </div>

      <div className="absolute right-0 bottom-0 z-0">
        <HUImageView image={{ url: backgroundCornerIllustration, hash: "" }} width="650px" height="775px" />
      </div>
    </div>
  )
}
